import { createFileRoute, useNavigate, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useAuth } from "@/components/AuthProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "@/components/ui/sonner";
import { logger } from "@/lib/logger";
import { getUserMessage } from "@/lib/errors";
import { useTheme } from "@/providers/ThemeProvider";
import { ClientMyceliumScene } from "@/features/landing/components/ClientMyceliumScene";
import { HolographicCard } from "@/features/landing/components/HolographicCard";
import { GoogleGLogo } from "@/features/landing/components/GoogleGLogo";

export const Route = createFileRoute("/auth/")({
  component: AuthPage,
});

function AuthPage() {
  const { signIn, signUp, signInWithGoogle, isAuthenticated, loading } = useAuth();
  const navigate = useNavigate();
  const { colorTheme } = useTheme();
  const [mode, setMode] = useState<"signin" | "signup">("signin");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!loading && isAuthenticated) {
      navigate({ to: "/dashboard" });
    }
  }, [loading, isAuthenticated, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);

    try {
      if (mode === "signin") {
        await signIn(email, password);
      } else {
        await signUp(email, password);
      }
    } catch (err) {
      const message = getUserMessage(err);
      setError(message);
      logger.error("Authentication failed", { error: err, mode });
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleGoogleSignIn = async () => {
    setError(null);
    try {
      await signInWithGoogle();
    } catch (err) {
      const message = getUserMessage(err);
      setError(message);
      logger.error("Google sign-in failed", { error: err });
      toast.error(message);
    }
  };

  if (loading) {
    return (
      <div className={`dark min-h-screen flex items-center justify-center bg-background theme-${colorTheme}`}>
        <p className="text-muted-foreground">Loading...</p>
      </div>
    );
  }

  return (
    <div className={`dark relative min-h-screen flex items-center justify-center px-4 overflow-hidden bg-background theme-${colorTheme}`}>
      {/* 3D wallpaper from the hero */}
      <div className="absolute inset-0 z-0">
        <ClientMyceliumScene className="h-full w-full" />
      </div>
      <div className="absolute inset-0 z-[1] bg-gradient-to-b from-background via-background/80 to-background" />
      <div className="absolute inset-0 z-[1] bg-[radial-gradient(circle_at_50%_30%,transparent_0%,var(--background)_70%)]" />

      {/* Ambient glow */}
      <div className="absolute inset-0 z-[2] pointer-events-none overflow-hidden opacity-30">
        <div className="absolute top-1/4 left-1/4 h-64 w-64 rounded-full bg-primary/20 blur-[100px] animate-pulse" />
        <div className="absolute bottom-1/4 right-1/4 h-72 w-72 rounded-full bg-primary/20 blur-[120px] animate-pulse" style={{ animationDelay: "2s" }} />
      </div>

      <div className="relative z-10 w-full max-w-md">
        <HolographicCard interactive className="p-1">
          <div className="p-8">
            <div className="text-center mb-8">
              <Link to="/home" className="inline-flex items-center gap-3">
                <img
                  src="/flowstate-logo.png"
                  alt="FlowState"
                  className="h-10 w-auto object-contain"
                />
                <span className="text-xl font-bold tracking-tight text-foreground">
                  FlowState
                </span>
              </Link>
              <h1 className="mt-6 text-2xl sm:text-3xl font-bold tracking-tight text-foreground">
                {mode === "signin" ? "Welcome back" : "Create your account"}
              </h1>
              <p className="mt-2 text-sm text-muted-foreground">
                {mode === "signin" ? "Sign in to continue" : "Sign up to get started"}
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              {error && (
                <div className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">
                  {error}
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <Input
                  id="password"
                  type="password"
                  required
                  minLength={6}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                />
              </div>

              <Button type="submit" className="w-full shadow-[0_0_30px_-12px_var(--primary)]" loading={submitting}>
                {mode === "signin" ? "Sign In" : "Sign Up"}
              </Button>
            </form>

            <div className="relative my-6">
              <div className="absolute inset-0 flex items-center">
                <span className="w-full border-t border-white/10" />
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-card/40 px-2 text-muted-foreground">
                  Or continue with
                </span>
              </div>
            </div>

            <Button
              type="button"
              variant="outline"
              className="w-full bg-white text-gray-900 hover:bg-gray-50 hover:text-gray-900 border-0 shadow-lg"
              onClick={handleGoogleSignIn}
            >
              <GoogleGLogo className="mr-3 h-5 w-5" />
              {mode === "signin" ? "Sign in with Google" : "Sign up with Google"}
            </Button>

            <p className="mt-6 text-center text-sm text-muted-foreground">
              {mode === "signin" ? (
                <>
                  Don&apos;t have an account?{" "}
                  <Button
                    type="button"
                    variant="link"
                    className="h-auto p-0 text-sm text-primary"
                    onClick={() => setMode("signup")}
                  >
                    Sign up
                  </Button>
                </>
              ) : (
                <>
                  Already have an account?{" "}
                  <Button
                    type="button"
                    variant="link"
                    className="h-auto p-0 text-sm text-primary"
                    onClick={() => setMode("signin")}
                  >
                    Sign in
                  </Button>
                </>
              )}
            </p>

            <Link
              to="/home"
              className="mt-4 block text-center text-sm text-muted-foreground hover:text-foreground transition-colors"
            >
              ← Back to Home
            </Link>
          </div>
        </HolographicCard>
      </div>
    </div>
  );
}
